/**
 * Deliberation Record — first-class signal capture for sessions.
 * Canon 23 §6.4 (Deviation D03).
 *
 * Every session has exactly one Deliberation Record: a structured container
 * that accumulates signal from four vectors and links it to specific items
 * and decisions:
 *
 *   1. Session chat log        — timestamped thread during live/open sessions
 *   2. Meeting recording       — audio/video (existing SessionRecording rows)
 *   3. Transcript              — post-session async processing, linked to items
 *   4. Rationale notes         — per-item, per-override annotations
 *
 * Design constraints (Canon 23 §6.4):
 * - Minimum participant friction. Recording is one field; transcript
 *   processing is asynchronous and never blocks session completion.
 * - OverrideJustification.reason is the ONLY mandatory signal input.
 * - Annotations can be added after the session closes — the record is
 *   never locked at session publish.
 * - One DeliberationRecord per session, created with the session,
 *   never deleted.
 *
 * Raw capture layer (recordings, live transcript segments) lives in
 * sessionRecording.ts; this module is the aggregation/enrichment layer.
 */

// ============================================================================
// Core types
// ============================================================================

/** Where a piece of signal originated. */
export type SignalSource = 'manual' | 'transcript_excerpt' | 'chat_excerpt' | 'recording';

/** Lifecycle of post-session transcript processing. Never blocks the session. */
export type TranscriptStatus = 'none' | 'processing' | 'available';

/**
 * A timestamped message in the session's chat log. Captured during
 * participant_live sessions (and open sessions that enable annotation
 * threads); facilitator notes in facilitator_live sessions land here too.
 */
export interface DeliberationMessage {
  id: string;
  sessionId: string;
  authorId: string;
  authorName: string;
  content: string;
  createdAt: string;
}

/**
 * Rationale attached to a specific item — the "why" behind how the item
 * was judged. May be entered manually during or after the session, or
 * sourced from a transcript/chat excerpt during post-session enrichment.
 */
export interface ItemAnnotation {
  id: string;
  sessionId: string;
  itemId: string;
  authorId: string;
  content: string;
  source: SignalSource;
  /** When source is transcript_excerpt: offset reference into the recording (e.g. "00:14:32"). */
  transcriptTimestamp?: string;
  /** When source is transcript_excerpt or recording: the SessionRecording this excerpt came from. */
  recordingId?: string;
  createdAt: string;
  updatedAt: string;
  /**
   * Set when the author retracted the note (mig 746). A retracted note is kept
   * in the record but hidden from the live view and from new report snapshots.
   */
  retractedAt?: string;
}

/**
 * Mandatory justification recorded when a facilitator overrides the
 * aggregate result for an item. The reason cannot be omitted — this is
 * the one signal input the system requires (Canon 23 §6.4).
 */
export interface OverrideJustification {
  id: string;
  sessionId: string;
  itemId: string;
  overriddenBy: string;
  /** Snapshot of what the aggregate said at the moment of override. */
  aggregateResult: unknown;
  /** What the facilitator chose instead. */
  overrideResult: unknown;
  /** Required. Server must reject overrides without a reason. */
  reason: string;
  createdAt: string;
}

/**
 * The umbrella record — one per session. Child collections (chatLog,
 * itemAnnotations, overrideJustifications) are stored normalized and
 * hydrated on read; recordings are linked by session via the existing
 * prioritization_session_recordings table.
 */
export interface DeliberationRecord {
  id: string;
  sessionId: string;
  workspaceId: string;
  /** SessionRecording ids associated with this session (derived, read-only). */
  recordingIds: string[];
  transcriptStatus: TranscriptStatus;
  chatLog: DeliberationMessage[];
  itemAnnotations: ItemAnnotation[];
  overrideJustifications: OverrideJustification[];
  /** Set when transcript segments have been linked to item annotations. */
  processedAt?: string;
  createdAt: string;
  updatedAt: string;
}

// ============================================================================
// Row shapes (snake_case, as returned from Supabase)
// ============================================================================

export interface DeliberationRecordRow {
  id: string;
  session_id: string;
  workspace_id: string;
  transcript_status: TranscriptStatus;
  processed_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface DeliberationMessageRow {
  id: string;
  deliberation_record_id: string;
  session_id: string;
  workspace_id: string;
  author_id: string;
  author_name: string;
  content: string;
  created_at: string;
}

export interface ItemAnnotationRow {
  id: string;
  deliberation_record_id: string;
  session_id: string;
  workspace_id: string;
  item_id: string;
  author_id: string;
  content: string;
  source: SignalSource;
  transcript_timestamp: string | null;
  recording_id: string | null;
  created_at: string;
  updated_at: string;
  retracted_at?: string | null;
  /** 831: a transcript passage the linker proposed stays 'candidate' until a person confirms or rejects it. */
  status?: AnnotationStatus;
}

/** 831: only 'confirmed' annotations are notes; the others are proposals waiting for, or turned down by, a person. */
export type AnnotationStatus = 'candidate' | 'confirmed' | 'rejected';

export interface OverrideJustificationRow {
  id: string;
  deliberation_record_id: string;
  session_id: string;
  workspace_id: string;
  item_id: string;
  overridden_by: string;
  aggregate_result: unknown;
  override_result: unknown;
  reason: string;
  created_at: string;
}

// ============================================================================
// Row ↔ domain mappers
// ============================================================================

export function deliberationMessageFromRow(row: DeliberationMessageRow): DeliberationMessage {
  return {
    id: row.id,
    sessionId: row.session_id,
    authorId: row.author_id,
    authorName: row.author_name,
    content: row.content,
    createdAt: row.created_at,
  };
}

export function itemAnnotationFromRow(row: ItemAnnotationRow): ItemAnnotation {
  return {
    id: row.id,
    sessionId: row.session_id,
    itemId: row.item_id,
    authorId: row.author_id,
    content: row.content,
    source: row.source,
    transcriptTimestamp: row.transcript_timestamp ?? undefined,
    recordingId: row.recording_id ?? undefined,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
    retractedAt: row.retracted_at ?? undefined,
  };
}

export function overrideJustificationFromRow(row: OverrideJustificationRow): OverrideJustification {
  return {
    id: row.id,
    sessionId: row.session_id,
    itemId: row.item_id,
    overriddenBy: row.overridden_by,
    aggregateResult: row.aggregate_result,
    overrideResult: row.override_result,
    reason: row.reason,
    createdAt: row.created_at,
  };
}

export function deliberationRecordFromRows(
  record: DeliberationRecordRow,
  recordingIds: string[],
  messages: DeliberationMessageRow[],
  annotations: ItemAnnotationRow[],
  overrides: OverrideJustificationRow[]
): DeliberationRecord {
  return {
    id: record.id,
    sessionId: record.session_id,
    workspaceId: record.workspace_id,
    recordingIds,
    transcriptStatus: record.transcript_status,
    chatLog: messages.map(deliberationMessageFromRow),
    itemAnnotations: annotations.map(itemAnnotationFromRow),
    overrideJustifications: overrides.map(overrideJustificationFromRow),
    processedAt: record.processed_at ?? undefined,
    createdAt: record.created_at,
    updatedAt: record.updated_at,
  };
}
